<template>
  <div>
    <div class="small text-right text-muted"
         v-if="showToggle">
      <a class="pointer text-muted"
         @click="selectFromPrice">
        {{ $t('select_from_price') }}
      </a>
    </div>
    <b-modal id="price-service-search-modal"
             hide-footer
             v-if="showModal"
             :title="$t('search')"
             @hidden="showModal = false"
             @show="onShow"
             size="md"
             centered>
      <div>
        <InputGroup :placeholder="$t('search')"
                    class="mb-3"
                    v-model="searchQ"/>
      </div>
      <div v-if="treeSearched" class="scroll-zone">
        <div v-for="(item, index) in treeSearched"
             :key="`serv-${index}`">

          <div class="font-weight-bold mb-2 pointer"
               @click="priceToggle(item.price.id)">
            <MinusSvg v-if="pricesOpened.includes(item.price.id) || searchQ"/>
            <PlusSvg v-else/>
            {{ item.price.title }}
          </div>
          <div v-if="pricesOpened.includes(item.price.id) || searchQ">
            <div v-for="categoryItem in item.categories"
                 class="pl-3"
                 :key="`serv-${index}-${categoryItem.id}`">
              <div class="font-weight-bold mb-2 pointer"
                   @click="categoryToggle(categoryItem.category.id)">
                <MinusSvg v-if="opened.includes(categoryItem.category.id) || searchQ"/>
                <PlusSvg v-else/>
                {{ categoryItem.category.title }}
              </div>
              <div v-if="opened.includes(categoryItem.category.id) || searchQ">
                <div v-for="service in categoryItem.services"
                     class="pl-3"
                     :key="`service-${service.id}`">
                  <CheckboxGroup :fid="`service-${index}-${service.id}`"
                                 class="sm mb-md-1 mb-2 multiline"
                                 :label="`${service.title} ${currency}`"
                                 @input="selectedOption"
                                 :checkboxvalue="service.id"
                                 v-model="selected"/>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
      <div class="d-flex pt-20 justify-content-between border-top">
        <div class="text-muted small">{{ $t('total_services_selected') }}: {{ selected.length }}</div>
        <div class="d-inline-flex gap-2">
          <button class="btn-themed btn-themed-squared gray" @click="resetSelection">
            {{ $t('reset') }}
          </button>
          <button class="btn-themed btn-themed-squared"
                  :disabled="!selected.length"
                  @click="save">
            {{ $t('select') }}
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script setup>
import {computed, getCurrentInstance, nextTick, onBeforeMount, ref} from "vue"
import CheckboxGroup from "@/components/form/CheckboxGroup.vue"
import MinusSvg from "@/assets/svg-vue/general/boxminus.svg.vue"
import PlusSvg from "@/assets/svg-vue/general/boxplus.svg.vue"
import { PatientService } from "@/services/api/patient.api.service"
import InputGroup from "@/components/form/InputGroup.vue"
import {copyObject} from "@/extensions/prototypes/prototypes"

const instance = (getCurrentInstance())
let bvModal = null

const emit = defineEmits(['selected'])

const props = defineProps({
  insuranceCompanyId: {
    type: Number,
    required: false
  },
  showToggle: {
    type: Boolean,
    default: true
  },
  multipleSelection: {
    type: Boolean,
    default: true
  },
  preSelected: {
    type: Array,
    default: () => []
  }
})

const showModal = ref(false)

const tree = ref(null)
const selected = ref([])
const opened = ref([])
const pricesOpened = ref([])
const searchQ = ref('')

const selectFromPrice = async () => {
  selected.value = []
  opened.value = []
  pricesOpened.value = []
  tree.value = null
  await loadServices()
  showModal.value = true
  await nextTick()
  bvModal.show('price-service-search-modal')
}

const loadServices = async () => {
  const res = await PatientService.getServicesTree(props.insuranceCompanyId)
  tree.value = res.data
}

const categoryToggle = (id) => {
  if(opened.value.includes(id)) {
    opened.value = opened.value.filter(item => item !== id)
  } else {
    opened.value.push(id)
  }
}

const priceToggle = (id) => {
  if(pricesOpened.value.includes(id)) {
    pricesOpened.value = pricesOpened.value.filter(item => item !== id)
  } else {
    pricesOpened.value.push(id)
  }
}

const servicesFlat = computed(() => {
  const arr = []
  const uniqueIds = new Set()
  tree.value.forEach(item => {
    item.categories.forEach(category => {
      category.services.forEach(service => {
        if(!uniqueIds.has(service.id)) {
          arr.push(service)
          uniqueIds.add(service.id)
        }
      })
    })
  })
  return arr
})

const selectedObjects = computed(() => {
  if(!tree.value) return []
  return servicesFlat.value.filter(item => selected.value.includes(item.id))
})

const treeSearched = computed(() => {
  if(!searchQ.value) return tree.value
  const searchLower = searchQ.value.toLowerCase()
  return tree.value.map(cat => {
    return {
      ...cat,
      categories: cat.categories.map(item => {
        const filteredServices = item.services.filter(service => service.title.toLowerCase().includes(searchLower))
        return {
          ...item,
          services: filteredServices
        }
      }).filter(item => item.services.length > 0)
    }
  }).filter(item => item.categories.length > 0)
})

const save = () => {
  emit('selected', selectedObjects.value)
  bvModal.hide('price-service-search-modal')
}

const selectedOption = () => {
  if(!props.multipleSelection) {
    save()
  }
}

const resetSelection = () => {
  selected.value = []
}

const currency = computed(() => window.localStorage.getItem('currency'))

onBeforeMount(() => {
  bvModal = instance?.ctx?._bv__modal
})

const onShow = () => {
  if(props.preSelected && props.preSelected.length) {
    selected.value = copyObject(props.preSelected)
  }
}

defineExpose({ selectFromPrice })
</script>

<style scoped lang="scss">
.scroll-zone {
  //max-height: calc(100vh - 56px - 81px - 20px - 36px - 40px - 20px - 62px);
  //overflow: auto;
  padding-bottom: 20px;
}
.pt-20 {
  padding-top: 20px;
}
@media screen and (max-width: 768px) {
  //.scroll-zone {
  //  max-height: calc(100vh - 47px - 70px - 24px - 1px - 48px);
  //}
}
</style>
